import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Http from "../../../Common/Http";
import IsLogin from "../../../Common/IsLogin";
import Edit from "./Edit";

const View = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await Http.get(`/project/edit/${id}`);
      console.log("API response data:", res.data);
      if (res.data && res.data.data) {
        console.log("Project data:", res.data.data);
        console.log("Reference links:", res.data.data.reference_links);
        console.log("Server credentials:", res.data.data.server_credentials);
        console.log("Documents:", res.data.data.documents);
        setProject(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching project details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  // Get status tag color
  const getStatusColor = (status) => {
    switch (status) {
      case "active":
        return "success";
      case "completed":
        return "info";
      case "on-hold":
        return "warning";
      case "cancelled":
        return "danger";
      default:
        return "secondary";
    }
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  // Calculate project duration
  const calculateDuration = (startDate, endDate) => {
    if (!startDate) return "No start date";
    if (!endDate) return "No end date";

    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    return `${diffDays} days`;
  };

  // Calculate project progress
  const calculateProgress = (startDate, endDate) => {
    if (!startDate || !endDate) return 0;

    const start = new Date(startDate);
    const end = new Date(endDate);
    const today = new Date();

    if (today < start) return 0;
    if (today > end) return 100;

    const totalDuration = end - start;
    const elapsedDuration = today - start;
    return Math.round((elapsedDuration / totalDuration) * 100);
  };

  // Handle delete project
  const handleDelete = async () => {
    if (window.confirm("Are you sure you want to delete this project?")) {
      try {
        await Http.delete(`/project/destroy/${id}`);
        navigate("/list-project");
      } catch (error) {
        console.error("Error deleting project:", error);
      }
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }}></i>
        <p className="mt-2">Loading project details...</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center p-5">
        <i className="pi pi-exclamation-triangle" style={{ fontSize: "3rem", color: "#f59e0b" }}></i>
        <p className="mt-2">Project not found or has been deleted.</p>
        <Link to="/list-project" className="btn btn-primary mt-3">
          Back to Projects
        </Link>
      </div>
    );
  }

  return (
    <div>
      <IsLogin />
      <div className="am-mainpanel">
        <div className="am-pagetitle d-flex justify-content-between align-items-center">
          <div>
            <h5 className="am-title">Project Details</h5>
            <p className="text-muted mb-0">View and manage project information</p>
          </div>
          <div>
            <Button label="Edit" icon="pi pi-pencil" className="p-button-warning mr-2" onClick={() => setEditDialogVisible(true)} />
            <Button label="Delete" icon="pi pi-trash" className="p-button-danger mr-2" onClick={handleDelete} />
            {/* <Link to="/list-project" className="btn btn-secondary">
              <i className="pi pi-arrow-left mr-1"></i> Back to List
            </Link> */}
            <Button label="Back to List" icon="pi pi-arrow-left" className="p-button-secondary mr-2" onClick={() => navigate("/list-project")} />
          </div>
        </div>

        <div className="am-pagebody">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h2 className="mb-1">{project.title}</h2>
                  <div className="d-flex align-items-center">
                    <Tag value={project.status} severity={getStatusColor(project.status)} />
                    <span className="ml-3 text-muted">
                      <i className="pi pi-calendar mr-1"></i> {formatDate(project.start_date)} - {formatDate(project.end_date)}
                    </span>
                    <span className="ml-3 text-muted">
                      <i className="pi pi-clock mr-1"></i> Duration: {calculateDuration(project.start_date, project.end_date)}
                    </span>
                  </div>
                </div>
              </div>

              <ul className="nav nav-tabs mb-4">
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "overview" ? "active" : ""}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("overview");
                    }}
                  >
                    Overview
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "references" ? "active" : ""}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("references");
                    }}
                  >
                    Reference Links
                    {project.reference_links && project.reference_links.length > 0 && (
                      <span className="badge bg-primary rounded-pill ml-1">{project.reference_links.length}</span>
                    )}
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "credentials" ? "active" : ""}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("credentials");
                    }}
                  >
                    Server Credentials
                    {project.server_credentials && project.server_credentials.length > 0 && (
                      <span className="badge bg-primary rounded-pill ml-1">{project.server_credentials.length}</span>
                    )}
                  </a>
                </li>
                <li className="nav-item">
                  <a
                    className={`nav-link ${activeTab === "documents" ? "active" : ""}`}
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      setActiveTab("documents");
                    }}
                  >
                    Documents
                    {project.documents && project.documents.length > 0 && (
                      <span className="badge bg-primary rounded-pill ml-1">{project.documents.length}</span>
                    )}
                  </a>
                </li>
              </ul>

              {activeTab === "overview" && (
                <div>
                  <div className="row mb-4">
                    <div className="col-md-8">
                      <Card title="Description" className="h-100">
                        <div dangerouslySetInnerHTML={{ __html: project.description }} />
                      </Card>
                    </div>
                    <div className="col-md-4">
                      <Card title="Project Information" className="h-100">
                        <div className="mb-3">
                          <h6 className="mb-1">Status</h6>
                          <Tag value={project.status} severity={getStatusColor(project.status)} />
                        </div>
                        <div className="mb-3">
                          <h6 className="mb-1">Start Date</h6>
                          <p className="mb-0">{formatDate(project.start_date)}</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="mb-1">End Date</h6>
                          <p className="mb-0">{formatDate(project.end_date)}</p>
                        </div>
                        <div className="mb-3">
                          <h6 className="mb-1">Duration</h6>
                          <p className="mb-0">{calculateDuration(project.start_date, project.end_date)}</p>
                        </div>
                        {project.end_date && (
                          <div>
                            <h6 className="mb-1">Progress</h6>
                            <div className="progress">
                              <div
                                className="progress-bar"
                                role="progressbar"
                                style={{ width: `${calculateProgress(project.start_date, project.end_date)}%` }}
                                aria-valuenow={calculateProgress(project.start_date, project.end_date)}
                                aria-valuemin="0"
                                aria-valuemax="100"
                              >
                                {calculateProgress(project.start_date, project.end_date)}%
                              </div>
                            </div>
                          </div>
                        )}
                      </Card>
                    </div>
                  </div>
                </div>
              )}

              {activeTab === "references" && (
                <div>
                  <Card title="Reference Links">
                    {project.reference_links && project.reference_links.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Title</th>
                              <th>URL</th>
                              <th>Actions</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.reference_links.map((link, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{link.title || "No title"}</td>
                                <td>
                                  <a href={link.url} target="_blank" rel="noopener noreferrer">
                                    {link.url}
                                  </a>
                                </td>
                                <td>
                                  <a href={link.url} target="_blank" rel="noopener noreferrer" className="btn btn-sm btn-info mr-1">
                                    <i className="pi pi-external-link"></i>
                                  </a>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <i className="pi pi-link-off" style={{ fontSize: "2rem", color: "#ccc" }}></i>
                        <p className="mt-2">No reference links available for this project.</p>
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {activeTab === "credentials" && (
                <div>
                  <Card title="Server Credentials">
                    {project.server_credentials && project.server_credentials.length > 0 ? (
                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>#</th>
                              <th>Server Name</th>
                              <th>URL</th>
                              <th>Username</th>
                              <th>Password</th>
                              <th>Additional Info</th>
                            </tr>
                          </thead>
                          <tbody>
                            {project.server_credentials.map((cred, index) => (
                              <tr key={index}>
                                <td>{index + 1}</td>
                                <td>{cred.server_name || "No name"}</td>
                                <td>
                                  {cred.url ? (
                                    <a href={cred.url} target="_blank" rel="noopener noreferrer">
                                      {cred.url}
                                    </a>
                                  ) : (
                                    "No URL"
                                  )}
                                </td>
                                <td>{cred.username || "No username"}</td>
                                <td>
                                  <span className="text-muted">••••••••</span>
                                </td>
                                <td>{cred.additional_info || "No additional info"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <i className="pi pi-server" style={{ fontSize: "2rem", color: "#ccc" }}></i>
                        <p className="mt-2">No server credentials available for this project.</p>
                      </div>
                    )}
                  </Card>
                </div>
              )}

              {activeTab === "documents" && (
                <div>
                  <Card title="Documents">
                    {project.documents && project.documents.length > 0 ? (
                      <div className="row">
                        {project.documents.map((doc, index) => (
                          <div className="col-md-4 mb-3" key={index}>
                            <div className="card">
                              <div className="card-body">
                                <div className="d-flex align-items-center">
                                  <i
                                    className={`pi ${
                                      doc.file_type === "pdf"
                                        ? "pi-file-pdf"
                                        : doc.file_type === "doc" || doc.file_type === "docx"
                                        ? "pi-file-word"
                                        : doc.file_type === "xls" || doc.file_type === "xlsx"
                                        ? "pi-file-excel"
                                        : doc.file_type === "jpg" || doc.file_type === "png" || doc.file_type === "gif"
                                        ? "pi-image"
                                        : "pi-file"
                                    } mr-2`}
                                    style={{ fontSize: "2rem" }}
                                  ></i>
                                  <div>
                                    <h6 className="mb-0">{doc.title || "Document"}</h6>
                                    <small className="text-muted">
                                      {doc.file_type ? doc.file_type.toUpperCase() : "Unknown"} -{" "}
                                      {doc.file_size ? (doc.file_size / 1024).toFixed(2) + " KB" : "Unknown size"}
                                    </small>
                                  </div>
                                </div>
                                <div className="mt-3">
                                  <a
                                    href={`/storage/${doc.file_path}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-info mr-1"
                                  >
                                    <i className="pi pi-download mr-1"></i> Download
                                  </a>
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center p-4">
                        <i className="pi pi-file" style={{ fontSize: "2rem", color: "#ccc" }}></i>
                        <p className="mt-2">No documents available for this project.</p>
                      </div>
                    )}
                  </Card>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Dialog
        header="Edit Project"
        visible={editDialogVisible}
        style={{ width: "70vw", zIndex: 1000 }}
        onHide={() => setEditDialogVisible(false)}
        dismissableMask
      >
        <Edit rowData={project} setVisible={setEditDialogVisible} fetchData={fetchData} />
      </Dialog>
    </div>
  );
};

export default View;
